import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { Button } from "../../components/ui/button";
import Modal from "../../components/Modal";
import { DataTable } from "../../components/DataTable";
import { requestService, requestDataMap } from "../../services";
import ShortenedText from "../../components/ShortenedText";
import LoadingImage from "../../components/LoadingImage.js";
import { createItemStore } from "../../stores/createItemStore";
import { toast } from "react-toastify";
import StatusBadge from "../../components/table/StatusBadge"

const columns = [
  {
    accessorKey: "dateCreated",
    header: "Date",
    cell: ({ row }) => <span>{row.getValue("dateCreated").split(",")[0]}</span>,
  },
  {
    accessorKey: "requestId",
    header: "Request ID",
    cell: ({ row }) => (
      <span>
        <ShortenedText text={row.getValue("requestId")} />
      </span>
    ),
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => {
      const type = row.getValue("type");
      return (
        <div className="py-2 text-sm text-[#111827]">
          <span
            className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
              type === "Transaction"
                ? "text-blue-600 bg-white border border-blue-500"
                : "text-gray-500 bg-white border border-gray-500"
            }`}
          >
            {type}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "profileName",
    header: "Requester Profile",
    cell: ({ row }) => <span>{row.getValue("profileName")}</span>,
  },
  {
    accessorKey: "pocName",
    header: "POC",
    cell: ({ row }) => <span>{row.getValue("pocName")}</span>,
  },
  // {
  //   accessorKey: "communityName",
  //   header: "Community",
  //   cell: ({ row }) => <span>{row.getValue("communityName")}</span>,
  // },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "requests";
const useItems = createItemStore(
  storeName,
  (request) => request.requestId,
  () =>
    requestService.getRequests(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      "T"
    ),
  requestDataMap
);
// ========================================================

export default function Transaction() {
  const navigate = useNavigate();
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleConfirmApprove = async () => {
    try {
      if (!selectedRequestId) {
        console.error("No requestId selected");
        return;
      }

      const response = await requestService.acceptRequest({
        RequestId: selectedRequestId,
        Comment: "",
      });

      fetchItems();
      console.log("Request approved:", response);
      toast.success("🎉 Request has been approved successfully!", {
        autoClose: 2000,
      });
      setShowApproveModal(false);
      setReload((prev) => !prev);
      setSelectedRequestId(null);
    } catch (error) {
      console.error("Failed to approve request:", error);
      toast.error("❌ Failed to approve request", {
        autoClose: 2000,
      });
    }
  };

  const handleConfirmReject = async () => {
    try {
      if (!selectedRequestId) {
        console.error("No requestId selected");
        return;
      }

      const response = await requestService.rejectRequest({
        RequestId: selectedRequestId,
        Comment: "Request rejected.",
      });

      fetchItems();
      console.log("Request rejected:", response);
      toast.success("🎉 Request has been rejected successfully!", {
        autoClose: 2000,
      });
      setShowRejectModal(false);
      setReload((prev) => !prev);
      setSelectedRequestId(null);
    } catch (error) {
      console.error("Failed to reject request:", error);
      toast.error("❌ Failed to reject request", {
        autoClose: 2000,
      });
    }
  };

  const actions = (row) => [
    {
      label: "View Details",
      icon: <FaEye />,
      onClick: () => navigate(`/pending-requests/view/${row.requestId}`),
      variant: "dropPlain",
    },
    // {
    //   label: "Approve",
    //   icon: <FaCheckCircle />,
    //   onClick: () => {
    //     setSelectedRequestId(row.requestId);
    //     setShowApproveModal(true);
    //   },
    //   variant: "dropGreen",
    // },
    // {
    //   label: "Reject",
    //   icon: <FaTimesCircle />,
    //   onClick: () => {
    //     setSelectedRequestId(row.requestId);
    //     setShowRejectModal(true);
    //   },
    //   variant: "dropRed",
    // },
  ];

  const [reload, setReload] = useState(false);

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { isLoading, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );
  const data = getSortedFiltered(
    (request) =>
      request.status === "Pending" &&
      request.type === "Transaction Review"
  );
  const showLoading = data.length === 0 && isLoading;
  // console.log(`${storeName}:`, data);
  useEffect(() => {
    fetchItems({}, [
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      "T",
    ]);
  }, [reload]);
  // ========================================================

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 sm:p-8">
          <div className="bg-white shadow rounded-lg md:max-w-[calc(100vw-375px)] p-4 sm:p-6 text-sm md:mt-0">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                Pending Request List
              </div>
            </div>
            <hr />

            <div className="overflow-x-auto w-full ">
              {showLoading ? (
                // {getAllRequests.loading ? (
                <LoadingImage />
              ) : data.length > 0 ? (
                // ) : getAllRequests.data.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={data}
                  // data={filteredRequests}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  No requests found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Approve Request"
        description="Are you sure you want to Approve this request?"
        icon={<FaCheckCircle className="text-green-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowApproveModal(false)}
            >
              No, Cancel
            </Button>
            <Button onClick={handleConfirmApprove} disabled={loading}>
              {loading ? "Approving..." : "Yes, Approve this Request"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Request"
        description="Are you sure you want to reject this request?"
        icon={<FaTimesCircle className="text-red-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowRejectModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmReject}
              disabled={loading}
            >
              {loading ? "Rejecting..." : "Yes, Reject this Request"}
            </Button>
          </div>
        }
      ></Modal>
    </div>
  );
}
